import { Link } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { TEST_IDS } from "@/constants/testIds";
import { Broadcast, Disc, FilmStrip, MusicNotes, Play, Radio } from "@phosphor-icons/react";
import { usePlayer } from "@/contexts/PlayerContext";
import TrackCard from "@/components/TrackCard";

const VISUALIZER_BARS = [22, 48, 32, 68, 42, 82, 54, 96, 60, 76, 44, 92, 50, 70, 35, 62, 80, 44, 92, 58, 74, 38, 88, 56, 100, 64, 78, 48, 90, 52, 72, 38];

const placeholderArt = (from, to, label = "Recho") => {
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 640 360'><defs><linearGradient id='g' x1='0' x2='1' y1='0' y2='1'><stop stop-color='${from}'/><stop offset='1' stop-color='${to}'/></linearGradient><filter id='glow'><feGaussianBlur stdDeviation='8' result='b'/><feMerge><feMergeNode in='b'/><feMergeNode in='SourceGraphic'/></feMerge></filter></defs><rect width='640' height='360' fill='url(#g)'/><circle cx='500' cy='96' r='54' fill='#ffcf5f' opacity='.92'/><path d='M0 280 C120 210 220 260 340 215 C460 170 540 220 640 170 L640 360 L0 360Z' fill='#05070d' opacity='.56'/><g filter='url(#glow)' stroke='#09e7ff' stroke-width='3' opacity='.62'><path d='M80 270 C160 110 280 320 380 140 S560 280 600 120' fill='none'/></g><text x='40' y='315' font-family='Inter,Arial' font-size='34' fill='white' font-weight='800'>${label}</text></svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
};

const DEMO_AUDIO = "https://www.soundhelix.com/examples/mp3/SoundHelix-Song-1.mp3";

const DEMO_TRACKS = [
  { track_id: "demo-midnight", source: "jamendo", title: "Midnight Atlas", artist: "Creative Commons", album: "Chill · Ambient", artwork: placeholderArt("#ff512f", "#07111d", "Midnight Atlas"), duration: 228, stream_url: DEMO_AUDIO },
  { track_id: "demo-lagos", source: "archive", title: "Lagos Lounge", artist: "Jazz Collective", album: "Free Stream", artwork: placeholderArt("#082d3e", "#10c5d2", "Lagos Lounge"), duration: 248, stream_url: DEMO_AUDIO },
  { track_id: "demo-tokyo", source: "jamendo", title: "Tokyo Night", artist: "Neon District", album: "Electronic · MP3", artwork: placeholderArt("#170c36", "#ff2b77", "Tokyo Night"), duration: 214, stream_url: DEMO_AUDIO },
];

const DEMO_STATIONS = [
  { stationuuid: "demo-us", name: "The Vintage Signal", country: "US", tags: "Jazz", favicon: "", url: DEMO_AUDIO, url_resolved: DEMO_AUDIO },
  { stationuuid: "demo-fr", name: "Paris Café Radio", country: "FR", tags: "Lounge", favicon: "", url: DEMO_AUDIO, url_resolved: DEMO_AUDIO },
  { stationuuid: "demo-jp", name: "Tokyo Night Mix", country: "JP", tags: "Electronic", favicon: "", url: DEMO_AUDIO, url_resolved: DEMO_AUDIO },
  { stationuuid: "demo-br", name: "São Paulo Samba", country: "BR", tags: "Brazilian", favicon: "", url: DEMO_AUDIO, url_resolved: DEMO_AUDIO },
  { stationuuid: "demo-is", name: "Reykjavík Dub", country: "IS", tags: "Ambient", favicon: "", url: DEMO_AUDIO, url_resolved: DEMO_AUDIO },
];

const DEMO_VIDEOS = [
  { video_id: "demo-world", title: "World Sessions", channel: "Live Performances", thumbnail: placeholderArt("#3a111a", "#ff7a18", "World Sessions") },
  { video_id: "demo-archive", title: "Archive: Lost Tapes", channel: "Rare Radio Archives", thumbnail: placeholderArt("#2a160b", "#b87333", "Archive") },
  { video_id: "demo-tokyo-video", title: "Tokyo Chill", channel: "City Vibes", thumbnail: placeholderArt("#170c36", "#09e7ff", "Tokyo Chill") },
  { video_id: "demo-samba", title: "Samba Streets", channel: "Carnival Energy", thumbnail: placeholderArt("#2b1115", "#0ed0b8", "Samba") },
];

function SectionHeader({ eyebrow, title, to }) {
  return (
    <div className="flex items-end justify-between gap-4 mb-5">
      <div className="min-w-0">
        {eyebrow && <div className="recho-meta text-[10px] sm:text-xs mb-1.5">{eyebrow}</div>}
        <h2 className="font-serif-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white truncate">{title}</h2>
      </div>
      {to && (
        <Link to={to} className="shrink-0 font-mono text-[10px] sm:text-xs uppercase tracking-[0.16em] text-cyan-300 hover:text-white transition-colors">
          View all →
        </Link>
      )}
    </div>
  );
}

function HeroWorld() {
  return (
    <div className="relative recho-world glass-card-strong p-4 md:p-6">
      <div className="absolute inset-0 opacity-40 recho-world-glow" />
      <div className="visualizer" aria-hidden>
        {VISUALIZER_BARS.map((h, i) => <span key={i} style={{ height: `${h}%` }} />)}
      </div>
      <div className="absolute top-5 right-5 hidden xl:block w-[236px] rounded-3xl border border-white/10 bg-[#05070d]/75 backdrop-blur-2xl p-5 shadow-[0_28px_80px_rgba(0,0,0,.45)] z-10">
        <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#ff7a18] mb-4">Live now</div>
        <div className="font-serif-display text-2xl text-white italic mb-4">Recho Music</div>
        <div className="space-y-3 border-t border-white/10 pt-4">
          {[
            ["Lagos", "88.4"],
            ["Paris", "92.1"],
            ["Tokyo", "98.0"],
            ["Rio", "104.7"],
          ].map(([city, freq]) => (
            <div key={city} className="flex justify-between font-mono text-[11px] uppercase tracking-[0.12em] text-slate-300/75">
              <span>{city}</span><span className="text-[#ff4d1d] font-semibold">{freq}</span>
            </div>
          ))}
        </div>
        <Link to="/radios" className="mt-5 inline-flex font-mono text-[10px] uppercase tracking-[0.16em] text-cyan-300 hover:text-white">
          View all stations →
        </Link>
      </div>
    </div>
  );
}

function FeatureCard({ icon: Icon, tone, title, body }) {
  const toneClass = tone === "orange" ? "text-[#ff7a18] shadow-[0_0_28px_rgba(255,77,29,.24)]" : tone === "purple" ? "text-[#b56cff] shadow-[0_0_28px_rgba(181,108,255,.22)]" : "text-[#09e7ff] shadow-[0_0_28px_rgba(9,231,255,.22)]";
  return (
    <div className="glass-card group p-5 sm:p-6 min-h-[128px] hover:border-cyan-300/30 hover:-translate-y-1 transition-all duration-200">
      <div className="flex items-center gap-4">
        <div className={`w-14 h-14 rounded-2xl bg-white/[0.035] border border-white/10 flex items-center justify-center ${toneClass}`}>
          <Icon size={28} weight="duotone" />
        </div>
        <div className="min-w-0">
          <h3 className="font-serif-display text-xl sm:text-2xl font-bold leading-tight text-white">{title}</h3>
          <p className="recho-muted text-sm leading-relaxed mt-1">{body}</p>
        </div>
      </div>
    </div>
  );
}

function StationTile({ station, index, onPlay }) {
  const art = ["recho-art-vintage", "recho-art-sunset", "recho-art-tokyo", "recho-art-samba", "recho-art-lagos"][index % 5];
  return (
    <button
      key={station.stationuuid || station.name}
      onClick={() => onPlay(station)}
      className="glass-card group text-left overflow-hidden hover:border-orange-400/30 hover:-translate-y-1 transition-all duration-200"
    >
      <div className="flex items-stretch min-h-[88px]">
        <div className={`w-24 sm:w-28 shrink-0 ${art} relative overflow-hidden`}>
          <div className="absolute inset-0 bg-black/10" />
          <span className="absolute top-2 left-2 rounded-lg bg-white/10 px-2 py-1 font-mono text-[9px] uppercase text-white/90">{station.country || "WW"}</span>
        </div>
        <div className="flex-1 min-w-0 p-3 sm:p-4 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="font-serif-display text-base sm:text-lg font-semibold text-white leading-tight line-clamp-2">{station.name}</div>
            <div className="font-mono text-[10px] uppercase tracking-[0.12em] text-slate-400 mt-1 truncate">{(station.tags || "World").split(",")[0]}</div>
          </div>
          <span className="w-9 h-9 rounded-full border border-white/25 flex items-center justify-center text-white group-hover:bg-[#ff4d1d] group-hover:border-[#ff4d1d] shrink-0 transition-colors">
            <Play size={14} weight="fill" />
          </span>
        </div>
      </div>
    </button>
  );
}

function VideoTile({ video, index, onPlay }) {
  const art = ["recho-art-sunset", "recho-art-archive", "recho-art-tokyo", "recho-art-samba", "recho-art-lagos"][index % 5];
  return (
    <button onClick={() => onPlay(video)} className="glass-card group text-left overflow-hidden hover:border-cyan-300/30 hover:-translate-y-1 transition-all duration-200">
      <div className={`relative aspect-video ${video.thumbnail ? "bg-black" : art}`}>
        {video.thumbnail && <img src={video.thumbnail} alt="" className="w-full h-full object-cover opacity-85 group-hover:opacity-100 transition-opacity" loading="lazy" />}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
        <span className="absolute top-2 right-2 rounded-lg bg-black/60 px-2 py-1 font-mono text-[10px] text-white">{["4:32", "6:18", "3:47", "5:02", "4:15"][index % 5]}</span>
        <span className="absolute inset-0 m-auto w-12 h-12 rounded-full border border-white/30 bg-black/30 backdrop-blur flex items-center justify-center text-white group-hover:bg-[#ff4d1d] group-hover:border-[#ff4d1d] transition-colors">
          <Play size={18} weight="fill" />
        </span>
      </div>
      <div className="p-3">
        <div className="font-serif-display text-base sm:text-lg font-semibold text-white leading-tight line-clamp-1">{video.title}</div>
        <div className="font-sans text-xs text-slate-400 truncate mt-0.5">{video.channel}</div>
      </div>
    </button>
  );
}

export default function Home() {
  const [topStations, setTopStations] = useState([]);
  const [trending, setTrending] = useState([]);
  const [freeTracks, setFreeTracks] = useState([]);
  const { playStation, playVideo, playTrack } = usePlayer();

  useEffect(() => {
    api.get("/radio/topvote", { params: { limit: 8 } }).then(r => setTopStations(r.data || [])).catch(() => {});
    api.get("/youtube/trending", { params: { region: "US", max_results: 8 } }).then(r => setTrending(r.data.items || [])).catch(() => {});
    api.get("/jamendo/popular", { params: { limit: 6 } }).then(r => setFreeTracks(r.data.items || [])).catch(() => {});
  }, []);

  const tracks = useMemo(() => (freeTracks.length ? freeTracks : DEMO_TRACKS), [freeTracks]);
  const stations = useMemo(() => (topStations.length ? topStations : DEMO_STATIONS), [topStations]);
  const videos = useMemo(() => (trending.length ? trending : DEMO_VIDEOS), [trending]);

  return (
    <div className="recho-app-bg min-h-screen text-white overflow-hidden">
      <section className="relative max-w-[1440px] mx-auto px-4 sm:px-8 pt-6 sm:pt-10 lg:pt-12 pb-8">
        <div className="absolute -top-20 -left-20 w-72 h-72 bg-[#ff4d1d]/20 blur-[110px] rounded-full" />
        <div className="absolute top-10 right-0 w-96 h-96 bg-[#09e7ff]/10 blur-[120px] rounded-full" />
        <div className="relative grid lg:grid-cols-12 gap-6 lg:gap-8 items-center">
          <div className="lg:col-span-5 xl:col-span-4 z-10">
            <div className="recho-meta text-[10px] sm:text-xs mb-4 text-[#ff7a18]">Est. 2026 · Vol 1</div>
            <h1 className="font-serif-display text-5xl sm:text-6xl xl:text-7xl font-bold leading-[0.92] tracking-tight text-white text-glow-orange">
              Tune the world.<br />
              <span className="text-gradient-orange">Anywhere it sings.</span>
            </h1>
            <p className="mt-5 text-base sm:text-lg text-slate-300/100 leading-relaxed max-w-xl">
              A polished NeurAlpha-ready atlas of free radio, MP3s and music videos. Drift from Lagos jazz lounges to Tokyo night-mixes, Reykjavík dub stations to São Paulo samba — with one persistent player.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/music" data-testid={TEST_IDS.home.heroCta} className="neon-button px-5 sm:px-7 py-3.5 font-mono text-[11px] sm:text-xs uppercase tracking-[0.16em] inline-flex items-center gap-2.5">
                <Play size={15} weight="fill" /> Start listening
              </Link>
              <Link to="/radios" data-testid={TEST_IDS.home.exploreRadios} className="orange-outline-button px-5 sm:px-7 py-3.5 font-mono text-[11px] sm:text-xs uppercase tracking-[0.16em] inline-flex items-center gap-2.5">
                <Radio size={16} /> Radio
              </Link>
              <Link to="/videos" data-testid={TEST_IDS.home.exploreVideos} className="outline-button px-5 sm:px-7 py-3.5 font-mono text-[11px] sm:text-xs uppercase tracking-[0.16em] inline-flex items-center gap-2.5">
                <FilmStrip size={16} /> Videos
              </Link>
            </div>
          </div>
          <div className="lg:col-span-7 xl:col-span-8">
            <HeroWorld />
          </div>
        </div>
      </section>

      <section className="max-w-[1440px] mx-auto px-4 sm:px-8 pb-6">
        <div className="grid md:grid-cols-3 gap-3 sm:gap-4">
          <FeatureCard icon={Broadcast} tone="orange" title="40,000+ stations" body="From every nation. Open Radio Browser network." />
          <FeatureCard icon={MusicNotes} tone="cyan" title="Free MP3 atlas" body="Creative-Commons artists & public-domain archives." />
          <FeatureCard icon={Disc} tone="purple" title="Always with you" body="A persistent receiver follows you across the app." />
        </div>
      </section>

      <section className="max-w-[1440px] mx-auto px-4 sm:px-8 pb-8">
        <div className="glass-card-strong neon-border p-4 sm:p-6">
          <div className="grid lg:grid-cols-12 gap-5 items-center">
            <div className="lg:col-span-4">
              <div className="recho-meta text-[10px] mb-2">First-time user path</div>
              <h2 className="font-serif-display text-2xl sm:text-3xl font-bold leading-tight text-white">Listen in under ten seconds.</h2>
              <p className="text-sm text-slate-300/70 leading-relaxed mt-2">Open the app, choose Radio / Music / Video, then keep browsing while the bottom receiver follows you.</p>
            </div>
            <div className="lg:col-span-8 grid sm:grid-cols-3 gap-3">
              {[
                ["01", "Pick a source", "Radio, free MP3, Archive or YouTube music."],
                ["02", "Press play", "Large tap targets and persistent controls."],
                ["03", "Save later", "Favorites, history and public playlist sharing."],
              ].map(([n, title, body]) => (
                <div key={n} className="rounded-2xl border border-white/10 bg-white/[0.035] p-4 hover:bg-white/[0.055] transition-colors">
                  <div className="recho-meta text-[10px] mb-3">{n}</div>
                  <h3 className="font-serif-display text-xl font-semibold text-white mb-1.5">{title}</h3>
                  <p className="text-sm text-slate-300/70 leading-relaxed">{body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-[1440px] mx-auto px-4 sm:px-8 py-5 sm:py-6">
        <SectionHeader title="Free music, today" to="/music" />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          {tracks.slice(0, 6).map(t => (
            <TrackCard key={t.track_id || t.id || t.title} track={t} onPlay={playTrack} onFav={() => {}} isFav={false} />
          ))}
        </div>
      </section>

      <section className="max-w-[1440px] mx-auto px-4 sm:px-8 py-5 sm:py-6">
        <SectionHeader title="Most tuned today" to="/radios" />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
          {stations.slice(0, 5).map((s, i) => (
            <StationTile key={s.stationuuid || s.name} station={s} index={i} onPlay={playStation} />
          ))}
        </div>
      </section>

      <section className="max-w-[1440px] mx-auto px-4 sm:px-8 py-5 sm:py-8 pb-36 md:pb-32">
        <SectionHeader title="Trending videos" to="/videos" />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {videos.slice(0, 4).map((v, i) => (
            <VideoTile key={v.video_id || v.title} video={v} index={i} onPlay={playVideo} />
          ))}
        </div>
      </section>
    </div>
  );
}
